import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { useQueryClient } from "@tanstack/react-query";
import { File, Paperclip, X, Send } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import notify from "@/services/notify.tsx";

const MAX_COMMENT_INPUT_HEIGHT = 128;

interface FormComentarioProps {
  type: string;
  parentId: any;
}

function resizeCommentInput(element: HTMLTextAreaElement) {
  element.style.height = "auto";
  const nextHeight = Math.min(element.scrollHeight, MAX_COMMENT_INPUT_HEIGHT);
  element.style.height = `${nextHeight}px`;
  element.style.overflowY =
    element.scrollHeight > MAX_COMMENT_INPUT_HEIGHT ? "auto" : "hidden";
}

export default function FormComentario({
  type,
  parentId,
}: FormComentarioProps) {
  const { register, handleSubmit, reset } = useForm();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const contentField = register("content", {
    onChange: (event) =>
      resizeCommentInput(event.target as HTMLTextAreaElement),
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (payload: any) => {
    if (!payload.content.trim() && selectedFiles.length === 0) {
      notify.warning("Digite um comentário ou anexe um arquivo.");
      return;
    }

    const toastId = notify.loading("Enviando comentário...");
    try {
      setIsSubmitting(true);

      const formData = new FormData();

      if (type === "tarefa") {
        formData.append("tarefa_id", parentId);
      } else if (type === "nota") {
        formData.append("nota_id", parentId);
      }

      if (payload.content) {
        formData.append("content", payload.content);
      }

      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const res = await ServiceResumoDesk.addComentarios(formData);

      if (res.status === 201) {
        notify.update(toastId, "success", "Comentário enviado!");
        reset();
        requestAnimationFrame(() => {
          if (commentInputRef.current) resizeCommentInput(commentInputRef.current);
        });
        setSelectedFiles([]);
        // refetch();
        queryClient.invalidateQueries({ queryKey: ["comentarios-tarefas"] });
        queryClient.invalidateQueries({ queryKey: ["dataNotificacoes"] });
      }
    } catch (error: any) {
      notify.update(
        toastId,
        "error",
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Erro ao enviar comentário",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-2 h-full justify-end items-end w-full"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Preview dos arquivos selecionados */}
      {selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border">
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border text-sm"
            >
              <File className="w-4 h-4 text-gray-500" />
              <span className="font-medium truncate max-w-32" title={file.name}>
                {file.name}
              </span>
              <span className="text-xs text-gray-500">
                {(file.size / 1024).toFixed(1)} KB
              </span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="text-red-500 hover:text-red-700 ml-1"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end w-full gap-x-1 p-2">
        <textarea
          {...contentField}
          ref={(element) => {
            contentField.ref(element);
            commentInputRef.current = element;
          }}
          rows={1}
          className="min-h-10 max-h-32 w-full resize-none overflow-y-hidden rounded-lg bg-white px-2 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-azul-400"
          placeholder="Comentário..."
        />

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          className="hidden"
          accept="*/*"
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="h-10 w-10 flex-shrink-0 bg-gray-200 hover:bg-gray-300 flex items-center justify-center rounded-lg transition-colors"
          title="Anexar arquivos"
        >
          <Paperclip className="w-4 h-4 text-gray-600" />
        </button>

        <button
          disabled={isSubmitting}
          type="submit"
          className="h-10 w-10 flex-shrink-0 bg-[#153ECE] flex items-center justify-center rounded-lg disabled:opacity-50"
        >
          <Send className="text-xs text-white w-4" />
        </button>
      </div>
    </form>
  );
}
